import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useHistory } from '~/history'
import { bewizrCoursesStore } from '~/stores'
import { recallLearningTrackForCourse } from '~/ui/app/bewizr'
import { Breadcrumb } from '~/ui/app/navigation/types'
import { observer } from '~/ui/component'
import { EmptyOrFetching, Scroller } from '~/ui/components'
import { AppLayoutConfig } from '~/ui/layouts'
import BeWizrCourseView from './BeWizrCourseView'

export interface BeWizrCourseParams {
  slug: string
}

export type Props = RouteComponentProps<BeWizrCourseParams>

const BeWizrCourseScreen = observer('BeWizrCourseScreen', (props: Props) => {

  const {slug} = props.match.params

  const document    = bewizrCoursesStore.courses.document(slug)
  const course      = document.data
  const fetchStatus = document.fetchStatus

  React.useEffect(() => {
    document?.fetch()
  }, [document])

  // If there's no live in-app navigation history (e.g. we just returned from the course
  // content, which opens in a separate tab), fall back to the learning track we last saw
  // this course opened from, so there's still a way back.
  const history = useHistory()

  const defaultBreadcrumbs = React.useMemo((): Breadcrumb[] | undefined => {
    if (history.stack.length >= 2) { return undefined }

    const learningTrack = recallLearningTrackForCourse(slug)
    if (learningTrack == null) { return undefined }

    return [
      {caption: learningTrack.title, href: `/bewizr-courses/learning-tracks/-/${learningTrack.id}`},
      {caption: course?.title ?? '', href: null},
    ]
  }, [history.stack.length, slug, course?.title])

  //------
  // Rendering

  function render() {
    return (
      <>
        <AppLayoutConfig
          title={course?.title}
          defaultBreadcrumbs={defaultBreadcrumbs}
          fetchStatus={fetchStatus}
        />
        {renderContent()}
      </>
    )
  }

  function renderContent() {
    if (course?.items == null || fetchStatus !== 'done') {
      return renderEmpty()
    }

    return (
      <Scroller>
        <BeWizrCourseView course={course}/>
      </Scroller>
    )
  }

  function renderEmpty() {
    return (
      <EmptyOrFetching
        flex
        status={fetchStatus}
      />
    )
  }

  return render()

})

export default BeWizrCourseScreen